import { z } from 'zod';
import type { de } from '@/i18n/de';

/**
 * URL-Vertrag des Dashboards (Plan 9.2): alle Seitenpfade und ihre Suchparameter an einer Stelle. Die Seiten der
 * Welle 2 liefern nur die Datei `routes/<file>` (Standardexport); Pfade und Suchparameter ändert allein W1-35
 * (Nachtrag auf Rückmeldung der Web-WPs).
 *
 * Suchparameter sind großzügig beim Lesen und streng beim Ergebnis: unbekannte Schlüssel entfallen, ungültige
 * Werte entfallen einzeln (es gilt der Standard der Seite), und nie wird eine Ausnahme geworfen. Das Lesen aus
 * der Adressleiste liefert Zahlen für Ziffernfolgen und Wahrheitswerte für „true“/„false“; die Schemas erwarten
 * das (Text-Felder nehmen Zahlen als Text an).
 */

type PageTitle = keyof typeof de.pages;

const text = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'number' ? String(value) : value),
    z.string().min(1).max(max),
  );

const day = z.iso.date();
const uuid = z.uuid();
// Sprache der Stimme und der Floskel-Regeln (`LANGUAGE_VALUES` der Sichtbarkeit; Nachtrag W2-29)
const language = z.enum(['de', 'en']);

const CONNECTION = z.enum(['connected', 'invited', 'invited_by', 'none']);
const BAND = z.enum(['fleeting', 'loose', 'familiar', 'close', 'unrated']);
const SORT = z.enum(['strength', 'last_interaction', 'name']);

/**
 * Prüft jedes bekannte Feld einzeln: gültige Werte bleiben, alles andere entfällt. Das Ergebnis ist stets ein
 * Objekt mit höchstens den Schlüsseln des Schemas.
 */
export function lenientSearch<S extends z.ZodObject>(schema: S) {
  const shape = schema.shape as Record<string, z.ZodType>;
  return (raw: Record<string, unknown>): z.output<S> => {
    const result: Record<string, unknown> = {};
    const input = raw !== null && typeof raw === 'object' ? raw : {};
    for (const [key, field] of Object.entries(shape)) {
      if (Object.hasOwn(input, key)) {
        const parsed = field.safeParse(input[key]);
        if (parsed.success && parsed.data !== undefined) {
          result[key] = parsed.data;
        }
      }
    }
    return result as z.output<S>;
  };
}

function entry<const Path extends string, const Shape extends z.ZodRawShape>(
  path: Path,
  file: string,
  title: PageTitle,
  shape: Shape,
) {
  const search = z.object(shape);
  return { id: path, path, file, title, search, validateSearch: lenientSearch(search) };
}

export type RouteEntry = ReturnType<typeof entry>;

const none = {} as const;

export const ENTRIES = {
  heute: entry('/', 'heute/index.page.tsx', 'heute', {
    scope: z.union([z.literal('all'), uuid]).optional(),
  }),
  menschen: entry('/menschen', 'menschen/index.page.tsx', 'menschen', {
    q: text(200).optional(),
    connection: CONNECTION.optional(),
    band: BAND.optional(),
    inExchange: z.boolean().optional(),
    sort: SORT.optional(),
  }),
  menschenDetail: entry('/menschen/$personId', 'menschen/detail.page.tsx', 'person', {
    bereich: z.enum(['verlauf', 'herkunft', 'datenschutz']).optional(),
  }),
  gespraechDetail: entry(
    '/gespraeche/$conversationId',
    'gespraeche/detail.page.tsx',
    'gespraech',
    none,
  ),
  netzwerk: entry('/netzwerk', 'netzwerk/index.page.tsx', 'netzwerk', {
    // dieselben Werte wie `NetworkSummaryQuery.range` der Route (Nachtrag N-F2/VE); ungültig = Standard der Seite (12m)
    range: z.enum(['30d', '90d', '12m', '24m']).optional(),
    zielgruppe: uuid.optional(),
    ansicht: z.enum(['karte', 'liste']).optional(),
  }),
  konto: entry('/konto', 'konto/index.page.tsx', 'konto', none),
  einstellungen: entry('/einstellungen', 'einstellungen/index.page.tsx', 'einstellungen', none),
  freigaben: entry('/einstellungen/freigaben', 'einstellungen/freigaben.page.tsx', 'freigaben', {
    gruppe: z.enum(['ausgehend', 'intern', 'deaktiviert']).optional(),
    filter: text(60).optional(),
  }),
  datenschutz: entry('/datenschutz', 'datenschutz/index.page.tsx', 'datenschutz', {
    reiter: z.enum(['anfragen', 'hinweise', 'strategie', 'protokoll']).optional(),
  }),
  verbindungen: entry('/verbindungen', 'verbindungen/index.page.tsx', 'verbindungen', {
    // Rückkehr von Microsoft 365 (W2-16m leitet auf `/verbindungen?m365=connected|denied|error`); Nachtrag W2-24
    m365: z.enum(['connected', 'denied', 'error', 'unavailable']).optional(),
  }),
  freigabe: entry('/freigabe/$operationId', 'freigabe/detail.page.tsx', 'freigabe', none),
  zielpersonen: entry('/zielpersonen', 'zielpersonen/index.page.tsx', 'zielpersonen', {
    // Reiter der Liste (W2-25); die Seite bildet die Werte auf `active`/`paused`/`archived` der Route ab
    status: z.enum(['aktiv', 'pausiert', 'archiviert']).optional(),
  }),
  zielpersonenDetail: entry(
    '/zielpersonen/$targetId',
    'zielpersonen/detail.page.tsx',
    'zielperson',
    none,
  ),
  // Zielfindung (TP2-Erweiterung, VB-Z4): drei Schritte, `profil` = Suchprofil, `schritt` = Abschnitt
  neueZielpersonen: entry(
    '/neue-zielpersonen',
    'neue-zielpersonen/index.page.tsx',
    'neueZielpersonen',
    {
      profil: uuid.optional(),
      schritt: z.enum(['suchen', 'einfuegen', 'web']).optional(),
    },
  ),
  nachrichten: entry('/nachrichten', 'nachrichten/index.page.tsx', 'nachrichten', {
    reiter: z
      .enum(['wartet_auf_dich', 'wartet_auf_andere', 'spaeter', 'alle', 'archiv'])
      .optional(),
    q: text(200).optional(),
    ansicht: uuid.optional(),
  }),
  nachrichtenDetail: entry(
    '/nachrichten/$conversationId',
    'nachrichten/detail.page.tsx',
    'nachricht',
    none,
  ),
  zielgruppen: entry('/zielgruppen', 'zielgruppen/index.page.tsx', 'zielgruppen', none),
  zielgruppenDetail: entry(
    '/zielgruppen/$audienceId',
    'zielgruppen/detail.page.tsx',
    'zielgruppe',
    none,
  ),
  ziele: entry('/ziele', 'ziele/index.page.tsx', 'ziele', none),
  ablaeufe: entry('/ablaeufe', 'ablaeufe/index.page.tsx', 'ablaeufe', none),
  einwilligung: entry('/einwilligung', 'einwilligung/index.page.tsx', 'einwilligung', {
    // Person, deren Einwilligung und Sperre die Seite zeigt (Nachtrag W2-27; Links aus Nachrichten und Personen)
    person: uuid.optional(),
  }),
  sichtbarkeit: entry('/sichtbarkeit', 'sichtbarkeit/index/index.page.tsx', 'sichtbarkeit', none),
  werkstatt: entry(
    '/sichtbarkeit/werkstatt/$contentItemId',
    'sichtbarkeit/werkstatt/index.page.tsx',
    'werkstatt',
    none,
  ),
  themen: entry('/sichtbarkeit/themen', 'sichtbarkeit/themen/index.page.tsx', 'themen', {
    // deutsche Wörter für die Zustände der API (`TopicView.status`); die Seite bildet sie ab (Nachtrag W2-28)
    status: z
      .enum([
        'vorgeschlagen',
        'angenommen',
        'in_arbeit',
        'veroeffentlicht',
        'archiviert',
        'verworfen',
      ])
      .optional(),
  }),
  kommentieren: entry(
    '/sichtbarkeit/kommentieren',
    'sichtbarkeit/kommentieren/index.page.tsx',
    'kommentieren',
    none,
  ),
  fragen: entry('/sichtbarkeit/fragen', 'sichtbarkeit/fragen/index.page.tsx', 'fragen', none),
  stimme: entry('/sichtbarkeit/stimme', 'sichtbarkeit/stimme/index.page.tsx', 'stimme', {
    sprache: language.optional(),
  }),
  regeln: entry('/sichtbarkeit/regeln', 'sichtbarkeit/regeln/index.page.tsx', 'regeln', {
    sprache: language.optional(),
  }),
  fakten: entry('/sichtbarkeit/fakten', 'sichtbarkeit/fakten/index.page.tsx', 'fakten', none),
  plan: entry('/sichtbarkeit/plan', 'sichtbarkeit/plan/index.page.tsx', 'plan', {
    von: day.optional(),
    bis: day.optional(),
    ansicht: z.enum(['woche', 'monat']).optional(),
  }),
  wirkung: entry('/sichtbarkeit/wirkung', 'sichtbarkeit/wirkung/index.page.tsx', 'wirkung', {
    von: day.optional(),
    bis: day.optional(),
  }),
} as const;

/** Alle Seiten mit eigener Datei, in der Reihenfolge des Plans. */
export const ROUTE_TABLE: readonly (typeof ENTRIES)[keyof typeof ENTRIES][] =
  Object.values(ENTRIES);

/** Pfad der Zustandsansicht (nur in Entwicklung und im E2E-Bau, nie in Produktion). */
export const STATES_PATH = '/_zustaende';

/** Pfad der Seite ohne Zugang (kein Laden des Benutzers, keine Navigation). */
export const NO_ACCESS_PATH = '/kein-zugang';
